import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, Sparkles, ArrowRight, Loader2, Wand2 } from "lucide-react";
import { errMsg, useSSA } from "../SSAContext";

const FAMILY_TONE = {
  "Strategy & Insights": "#6D28D9",
  "Search & Performance": "#0072CE",
  "Social, Content & Creative": "#DB2777",
  "CRM, Automation & Conversion": "#059669",
  "Experience, Data & Commerce": "#D97706",
  "Soft Services": "#0891B2",
  "Security & Front of House": "#4F46E5",
  "Hard Services & Compliance": "#EA580C",
  "Workplace & Sustainability": "#16A34A",
  "Integrated FM": "#0B2A4A",
};

function IntentBox({ category }) {
  const { call } = useSSA();
  const nav = useNavigate();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const go = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { setRes(await call("post", "/ai/intent", { text, category })); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={go} className="mt-6 max-w-2xl" data-testid="intent-form">
      <div className="flex items-center gap-2 bg-white/10 border border-white/20 rounded-xl p-1.5 focus-within:border-[#7DD3FC]">
        <Sparkles size={16} className="text-[#7DD3FC] ml-2 shrink-0" />
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={category === "FM" ? "Describe your need, e.g. “Office cleaning for our 3 Dutch sites, incumbent retender”" : "Describe your need, e.g. “Google Ads for our product launch in NL and Germany”"}
          className="flex-1 bg-transparent text-white placeholder:text-white/50 text-sm outline-none py-2"
          data-testid="intent-input"
        />
        <button disabled={busy || text.length < 4} className="inline-flex items-center gap-1.5 bg-[#00B4D8] hover:bg-[#22C3E3] text-[#0B2A4A] font-bold text-sm px-4 py-2 rounded-lg disabled:opacity-50" data-testid="intent-submit">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />} Find my service
        </button>
      </div>
      {res && (
        <div className="mt-3 rounded-lg bg-white text-slate-800 p-4 text-sm" data-testid="intent-result">
          <div className="flex items-center justify-between gap-3">
            <div>
              <span className="font-mono text-xs text-slate-500">{res.service_id}</span> · <span className="font-semibold">{res.confidence}% match</span>
              <div className="text-slate-600 text-xs mt-1">{res.reason}</div>
            </div>
            <button type="button" onClick={() => nav(`/services/s/${res.service_id}`)} className="shrink-0 bg-[#0B2A4A] text-white text-xs font-semibold px-3 py-2 rounded-md" data-testid="intent-open-service">Open brief</button>
          </div>
          {res.related?.length > 0 && <div className="text-xs text-slate-500 mt-2">Related: {res.related.join(", ")}</div>}
          <div className="text-[10px] text-slate-400 mt-1">{res.engine}</div>
        </div>
      )}
    </form>
  );
}

function ServiceCard({ s, i }) {
  const tone = FAMILY_TONE[s.family];
  return (
    <Link
      to={`/services/s/${s.id}`}
      data-testid={`service-card-${s.id}`}
      className="group bg-white rounded-xl border border-slate-200 overflow-hidden flex flex-col hover:border-slate-400 hover:-translate-y-0.5 hover:shadow-lg transition-[transform,box-shadow,border-color] duration-200 animate-in fade-in slide-in-from-bottom-2"
      style={{ animationDelay: `${i * 30}ms`, animationFillMode: "both" }}
    >
      <div className="relative h-40 overflow-hidden bg-slate-100">
        <img src={s.image} alt={s.name} loading="lazy" className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" data-testid={`service-card-img-${s.id}`} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B2A4A]/70 via-transparent to-transparent" />
        <span className="absolute left-3 bottom-3 font-mono text-[11px] font-bold text-white bg-black/40 backdrop-blur px-2 py-0.5 rounded">{s.id}</span>
        <span className="absolute right-3 top-3 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-white/90" style={{ color: tone }}>{s.family}</span>
      </div>
      <div className="p-5 flex flex-col flex-1">
      <h3 className="font-display font-semibold text-slate-900 leading-snug">{s.name}</h3>
      <p className="text-xs text-slate-600 mt-2 leading-relaxed flex-1">{s.summary}</p>
      <div className="flex flex-wrap gap-1 mt-3">
        {s.outputs.slice(0, 3).map((o) => <span key={o} className="text-[10px] bg-slate-100 text-slate-600 rounded px-1.5 py-0.5">{o}</span>)}
      </div>
      <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
        <span className="text-[11px] text-slate-500 italic">{s.pricing_note}</span>
        <ArrowRight size={14} className="text-slate-400 group-hover:text-slate-900 group-hover:translate-x-0.5 transition-transform" />
      </div>
      </div>
    </Link>
  );
}

export default function CatalogPage() {
  const { cat = "DM" } = useParams();
  const { catalogs, category, setCategory } = useSSA();
  useEffect(() => { if (cat !== category && ["DM", "FM"].includes(cat)) setCategory(cat); }, [cat, category, setCategory]);
  const catalog = catalogs[cat];
  const meta = catalog?.meta;
  const [q, setQ] = useState("");
  const [family, setFamily] = useState("");
  const [objective, setObjective] = useState("");
  const services = useMemo(() => catalog?.services || [], [catalog]);
  const objectives = useMemo(() => [...new Set(services.flatMap((s) => s.objectives))], [services]);
  const filtered = services.filter((s) =>
    (!family || s.family === family) && (!objective || s.objectives.includes(objective)) &&
    (!q || `${s.id} ${s.name} ${s.summary} ${s.channels.join(" ")} ${s.outputs.join(" ")}`.toLowerCase().includes(q.toLowerCase()))
  );
  return (
    <div data-testid={`ssa-catalog-page-${cat}`}>
      <div className="rounded-2xl bg-[#0B2A4A] text-white p-8 lg:p-10 relative overflow-hidden">
        {meta?.image && <img src={meta.image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-15" />}
        <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#00B4D8]/20 blur-3xl" />
        <div className="relative">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#7DD3FC] font-bold" data-testid="catalog-category-name">Strategic Services Aggregator · {meta?.name}</div>
        <h1 className="font-display text-4xl sm:text-5xl font-semibold mt-3 max-w-3xl leading-tight">{meta?.tagline}</h1>
        <p className="text-white/70 mt-4 max-w-2xl text-sm leading-relaxed">
          {services.length} scope-based services. {meta?.blurb} Infosys Procurement routes your request to 3–4 pre-qualified providers,
          and you receive up to two options with the Infosys Procurement Management Fee shown as a separate, transparent line.
        </p>
        <IntentBox category={cat} />
        </div>
      </div>

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search services, channels, outputs" className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 bg-white text-sm" data-testid="catalog-search" />
        </div>
        <select value={family} onChange={(e) => setFamily(e.target.value)} className="h-10 rounded-lg border border-slate-200 bg-white text-sm px-3" data-testid="catalog-family-filter">
          <option value="">All service families</option>
          {(catalog?.families || []).map((f) => <option key={f}>{f}</option>)}
        </select>
        {objectives.length > 0 && <select value={objective} onChange={(e) => setObjective(e.target.value)} className="h-10 rounded-lg border border-slate-200 bg-white text-sm px-3" data-testid="catalog-objective-filter">
          <option value="">All objectives</option>
          {objectives.map((o) => <option key={o}>{o}</option>)}
        </select>}
        <span className="text-xs text-slate-500 ml-auto" data-testid="catalog-count">{filtered.length} of {services.length} services</span>
      </div>

      <div className="mt-5 grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map((s, i) => <ServiceCard key={s.id} s={s} i={i} />)}
      </div>
    </div>
  );
}
